  /**
 * Benchmark.ts - Performance measurement using Blob URL WebWorker
 * Self-contained for IIFE portability
 */

import { PERFORMANCE_PROFILES } from './Constants';

export type PerformanceLevel = 'Low' | 'Mid' | 'High';

export interface BenchmarkResult {
  level: PerformanceLevel;
  score: number;
  executionTime: number;
}

// Worker code as a string for Blob URL creation
const WORKER_CODE = `
  self.onmessage = function(e) {
    const iterations = e.data.iterations || 1000000;
    const startTime = performance.now();
    
    // CPU-intensive calculation: repeated sqrt operations
    let result = 0;
    for (let i = 0; i < iterations; i++) {
      result += Math.sqrt(i * Math.random());
      result = result % 1000; // Prevent overflow
    }
    
    const endTime = performance.now();
    const executionTime = endTime - startTime;
    
    self.postMessage({
      executionTime,
      result // Include result to prevent dead code elimination
    });
  };
`;

// Re-export PERFORMANCE_PROFILES for consumers
export { PERFORMANCE_PROFILES } from './Constants';

/**
 * Thresholds for performance classification (in milliseconds)
 * Lower execution time = better performance
 */
const THRESHOLDS = {
  HIGH_THRESHOLD: 150,  // < 150ms = High
  MID_THRESHOLD: 400,   // 150-400ms = Mid, > 400ms = Low
};

export class Benchmark {
  private worker: Worker | null = null;
  private blobUrl: string | null = null;
  private hasRun: boolean = false;
  private cachedResult: BenchmarkResult | null = null;

  /**
   * Run the benchmark and return a performance level
   */
  async run(iterations: number = 1000000): Promise<BenchmarkResult> {
    // Return cached result if already run
    if (this.hasRun && this.cachedResult) {
      return this.cachedResult;
    }

    // Check for WebWorker support
    if (typeof Worker === 'undefined') {
      console.warn('[Atmosphere] WebWorker not supported, defaulting to Mid profile');
      this.cachedResult = { level: 'Mid', score: 50, executionTime: 250 };
      this.hasRun = true;
      return this.cachedResult;
    }

    try {
      const result = await this.runWorkerBenchmark(iterations);
      this.cachedResult = result;
      this.hasRun = true;
      return result;
    } catch (error) {
      console.warn('[Atmosphere] Benchmark failed, defaulting to Mid profile:', error);
      this.cachedResult = { level: 'Mid', score: 50, executionTime: 250 };
      this.hasRun = true;
      return this.cachedResult;
    } finally {
      this.cleanup();
    }
  }

  /**
   * Run benchmark in a WebWorker using Blob URL
   */
  private runWorkerBenchmark(iterations: number): Promise<BenchmarkResult> {
    return new Promise((resolve, reject) => {
      try {
        // Create Blob URL for worker
        const blob = new Blob([WORKER_CODE], { type: 'application/javascript' });
        this.blobUrl = URL.createObjectURL(blob);
        this.worker = new Worker(this.blobUrl);

        // Set timeout for slow devices
        const timeout = setTimeout(() => {
          this.cleanup();
          reject(new Error('Benchmark timeout'));
        }, 10000);

        this.worker.onmessage = (e) => {
          clearTimeout(timeout);
          const { executionTime } = e.data;
          
          // Classify performance level
          const level = this.classifyPerformance(executionTime);
          const score = this.calculateScore(executionTime);
          
          resolve({ level, score, executionTime });
        };

        this.worker.onerror = (error) => {
          clearTimeout(timeout);
          reject(error);
        };

        // Start benchmark
        this.worker.postMessage({ iterations });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Classify execution time into performance level
   */
  private classifyPerformance(executionTime: number): PerformanceLevel {
    if (executionTime < THRESHOLDS.HIGH_THRESHOLD) {
      return 'High';
    } else if (executionTime < THRESHOLDS.MID_THRESHOLD) {
      return 'Mid';
    } else {
      return 'Low';
    }
  }

  /**
   * Calculate a 0-100 score based on execution time
   */
  private calculateScore(executionTime: number): number {
    // Map execution time to a 0-100 score (lower time = higher score)
    const maxTime = 800; // Assume anything above 800ms is 0
    const score = Math.max(0, Math.min(100, ((maxTime - executionTime) / maxTime) * 100));
    return Math.round(score);
  }

  /**
   * Get the cached result (if available)
   */
  getCachedResult(): BenchmarkResult | null {
    return this.cachedResult;
  }

  /**
   * Check if benchmark has been run
   */
  hasBeenRun(): boolean {
    return this.hasRun;
  }

  /**
   * Clean up worker resources
   */
  private cleanup(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }

  /**
   * Get recommended config updates for a performance level
   */
  static getConfigForLevel(level: PerformanceLevel): {
    particleCount: number;
    snow: {
      accumulation: {
        rate: number;
        maxHeight: number;
        groundRenderThrottle: number;
      };
    };
  } {
    const profile = PERFORMANCE_PROFILES[level];
    return {
      particleCount: profile.particleCount,
      snow: {
        accumulation: {
          rate: profile.accumulationRate,
          maxHeight: profile.maxAccumulationHeight,
          groundRenderThrottle: profile.groundRenderThrottle,
        },
      },
    };
  }
}

// Singleton instance for easy access
export const benchmark = new Benchmark();

